/**
 * DOM component for toggling SVG layer visibility by data-layer-type attribute.
 *
 * Receives the already-mounted SVG element, indexes all [data-layer-type] elements
 * at construction time, and exposes setVisibleLayerTypes() to toggle their display.
 * Contains no business logic — the caller decides which layers are visible.
 *
 * @module nativeInteractiveSiteMap/components/svgLayerVisibility.dom
 */

/**
 * @param {SVGElement|null} svgElement
 * @returns {{ setVisibleLayerTypes: (layerNames: Set<string>) => void }}
 */
 function createSvgLayerVisibilityComponent(svgElement) {
  if (!svgElement) {
    return { setVisibleLayerTypes: () => {} };
  }

  // Index at construction: SVG is in the DOM when onReady fires.
  // Multiple elements can share the same data-layer-type, so each key maps to an array.
  const elementsByLayer = new Map();
  svgElement.querySelectorAll('[data-layer-type]').forEach((element) => {
    const layerType = element.dataset.layerType;
    if (!elementsByLayer.has(layerType)) {
      elementsByLayer.set(layerType, []);
    }
    elementsByLayer.get(layerType).push(element);
  });

  // Track last-known state per layer to skip redundant DOM writes.
  const lastVisibility = new Map();

  return {
    setVisibleLayerTypes(layerNames) {
      elementsByLayer.forEach((elements, layerName) => {
        const shouldShow = layerNames.has(layerName);
        if (lastVisibility.get(layerName) === shouldShow) {
          return;
        }
        lastVisibility.set(layerName, shouldShow);
        elements.forEach((element) => {
          element.style.display = shouldShow ? 'block' : 'none';
        });
      });
    },
  };
}

export default createSvgLayerVisibilityComponent;
